import { DataTable } from "@/components/data-table";
import { HeaderCell } from "@/components/header-cell";
import type { ColumnMeta } from "@/components/stable-table-header";
import { Button } from "@/components/ui/button";
import { cn, formatCurrency } from "@/lib/utils";
import type { Position } from "@/solana/programs/types";
import { PositionManager } from "@/solana/programs/position-manager";
import { usePrice, usePositionTpSl, usePositionLeverage } from "@/backend/store";
import type { ClosePositionData } from "@/solana/hooks/use-positions";
import {
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { useMemo, useCallback, memo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useProduct } from "@/features/product/product-context";
import { useProductOptions } from "@/features/product/product-options";
import { useMarketPrecision } from "@/hooks/use-price-precision";
import { Skull, Pencil, X } from "lucide-react";
import { PositionsApi, type TpSlTargetDto } from "@nordex/core";

// ============================================================================
// COLUMN METADATA - STABLE (defined once, never changes)
// 
// This metadata is used by StableTableHeader to render headers independently
// of TanStack Table's object recreation. Headers will NEVER re-render due to
// data changes - only when sorting state changes (user clicks a header).
// ============================================================================
const COLUMN_META: ColumnMeta[] = [
  { id: "market", headerLabel: "Market", align: "left", canSort: true },
  { id: "side", headerLabel: "Side", align: "left", canSort: true },
  { id: "positionValue", headerLabel: "Position Value", align: "right", canSort: false },
  { id: "size", headerLabel: "Size", align: "right", canSort: true },
  { id: "entryPrice", headerLabel: "Entry Price", align: "right", canSort: true },
  { id: "currentPrice", headerLabel: "Current Price", align: "right", canSort: false },
  { id: "leverage", headerLabel: "Leverage", align: "right", canSort: true },
  { id: "margin", headerLabel: "Position Margin", align: "right", canSort: true },
  { id: "unrealizedPnl", headerLabel: "Unrealized P&L", align: "right", canSort: false },
  { id: "liquidationPrice", headerLabel: "Liq. Price", align: "right", canSort: true },
  { id: "tpsl", headerLabel: "TP / SL", align: "right", canSort: false },
  { id: "actions", headerLabel: "Close", align: "right", canSort: false },
];

// Calculate distance to liquidation as percentage
function calculateDistanceToLiquidation(
  currentPrice: number,
  liquidationPrice: number,
  isLong: boolean
): number {
  if (liquidationPrice <= 0 || currentPrice <= 0) return 100;
  
  if (isLong) {
    // Long: liquidation when price drops to liq price
    return ((currentPrice - liquidationPrice) / currentPrice) * 100;
  } else {
    // Short: liquidation when price rises to liq price
    return ((liquidationPrice - currentPrice) / currentPrice) * 100;
  }
}

// Get warning level based on distance
function getWarningLevel(distancePercent: number): "safe" | "caution" | "danger" {
  if (distancePercent <= 2) return "danger";
  if (distancePercent <= 5) return "caution";
  return "safe";
}

const warningConfig = {
  safe: {
    icon: null,
    color: "",
    bgColor: "",
  },
  caution: {
    icon: null,
    color: "",
    bgColor: "",
  },
  danger: {
    icon: Skull,
    color: "text-ask-foreground",
    bgColor: "bg-ask/10",
  },
};

// ============================================================================
// PRICE-DEPENDENT CELLS - Zustand selectors (industry-standard pattern)
// 
// These cells use usePrice() which subscribes to a SINGLE price in Zustand.
// When BTC price changes, only BTC cells re-render - ETH cells are unaffected.
// Headers and action buttons NEVER re-render (they don't subscribe to prices).
// This is exactly how Binance, HyperLiquid, and Bybit work.
// ============================================================================

const PositionValueCell = memo(function PositionValueCell({ 
  symbol,
  size
}: { 
  symbol: string;
  size: number; 
}) {
  // Zustand selector - only this cell re-renders when THIS symbol's price changes
  const currentPrice = usePrice(symbol);
  const positionValue = size * currentPrice;
  
  return (
    <div className="text-right font-medium">
      ${formatCurrency(positionValue, 2)}
    </div>
  );
});

const CurrentPriceCell = memo(function CurrentPriceCell({ 
  symbol 
}: { 
  symbol: string;
}) {
  // Zustand selector - only this cell re-renders when THIS symbol's price changes
  const currentPrice = usePrice(symbol);
  // Get market precision dynamically from backend config
  const { quotePrecision } = useMarketPrecision(symbol);
  return (
    <div className="text-right font-medium">
      ${formatCurrency(currentPrice, quotePrecision)}
    </div>
  );
});

const UnrealizedPnlCell = memo(function UnrealizedPnlCell({ 
  position
}: { 
  position: Position; 
}) {
  // Zustand selector - only this cell re-renders when THIS symbol's price changes
  const currentPrice = usePrice(position.market);
  
  const unrealizedPnl = currentPrice > 0
    ? PositionManager.calculateUnrealizedPnl(position, currentPrice)
    : position.unrealizedPnl;

  const pnlPercent = currentPrice > 0
    ? PositionManager.calculatePnlPercentage(position, currentPrice)
    : 0;

  const isPositive = unrealizedPnl >= 0;

  return (
    <div
      className={cn(
        "text-right font-medium flex flex-col items-end",
        isPositive ? "text-bid-foreground" : "text-ask-foreground"
      )}
    >
      <span>
        {isPositive ? "+" : "-"}${formatCurrency(Math.abs(unrealizedPnl), 2)}
      </span>
      <span className="text-xs">
        ({isPositive ? "+" : ""}
        {pnlPercent.toFixed(2)}%)
      </span>
    </div>
  );
});

const LiqPriceCell = memo(function LiqPriceCell({
  symbol,
  liqPrice,
  isLong
}: {
  symbol: string;
  liqPrice: number | null;
  isLong: boolean;
}) {
  // Zustand selector - only this cell re-renders when THIS symbol's price changes
  const currentPrice = usePrice(symbol);
  // Get market precision dynamically from backend config
  const { quotePrecision } = useMarketPrecision(symbol);
  
  // No liquidation price = position can't be liquidated (over-collateralized in cross mode)
  if (liqPrice === null || liqPrice === 0) {
    return (
      <div className="text-right font-medium text-muted-foreground">
        -
      </div>
    );
  }
  
  const distance = calculateDistanceToLiquidation(currentPrice, liqPrice, isLong);
  const warningLevel = getWarningLevel(distance);
  const config = warningConfig[warningLevel];

  return (
    <div className={cn("text-right font-medium", config.color)}>
      ${formatCurrency(liqPrice, quotePrecision)}
    </div>
  );
});


// ============================================================================
// MEMOIZED STATIC CELLS - These don't depend on live market data
// They only re-render when their specific prop values change
// ============================================================================

// ============================================================================
// TP/SL CELL - Subscribes directly to Zustand store (like price cells)
//
// This cell uses usePositionTpSl() to subscribe directly to the store.
// When TP/SL updates in the store, only this cell re-renders - the table row
// doesn't need to re-render. This is the same pattern used by price cells.
//
// Now also supports partial TP/SL targets:
// - When standard TP/SL is set: Shows single TP/SL prices
// - When partial targets exist: Shows summary (e.g., "3 TP / 1 SL")
//
// This fixes the issue where TP/SL updates weren't reflecting immediately:
// - Before: Cell received data via props from row.original (stale due to memo)
// - After: Cell subscribes to store directly (always gets latest data)
// ============================================================================
const TpSlCell = memo(function TpSlCell({ 
  market,
  position,
  onEditTpSl,
  onClearTpSl,
}: { 
  market: string;
  position: Position;
  onEditTpSl?: (position: Position) => void;
  onClearTpSl?: (market: string, type: "tp" | "sl") => void;
}) {
  // Subscribe directly to Zustand store - like price cells do with usePrice()
  // This ensures we always have the latest TP/SL values from the store
  const { takeProfit, stopLoss } = usePositionTpSl(market);
  
  // State for partial targets
  const [partialTargets, setPartialTargets] = useState<TpSlTargetDto[]>([]);
  const [isLoadingPartial, setIsLoadingPartial] = useState(false);
  
  const hasTP = takeProfit !== undefined && takeProfit !== null && takeProfit > 0;
  const hasSL = stopLoss !== undefined && stopLoss !== null && stopLoss > 0;
  const hasStandardTpSl = hasTP || hasSL;
  
  // Fetch partial targets when standard TP/SL are not set
  // This handles the case where user is using partial mode
  useEffect(() => {
    let cancelled = false;
    
    // Only fetch if no standard TP/SL is set (indicates partial mode might be active)
    if (!hasStandardTpSl && position.size !== 0) {
      setIsLoadingPartial(true);
      PositionsApi.getTpSlTargets(market)
        .then((targets) => {
          if (!cancelled) {
            setPartialTargets(targets.filter(t => t.status === 'active'));
            setIsLoadingPartial(false);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPartialTargets([]);
            setIsLoadingPartial(false);
          }
        });
    } else {
      // Clear partial targets when standard mode is active
      setPartialTargets([]);
    }
    
    return () => {
      cancelled = true;
    };
  }, [market, hasStandardTpSl, position.size]);
  
  // Count partial targets by type
  const partialTpCount = partialTargets.filter(t => t.target_type === 'take_profit').length;
  const partialSlCount = partialTargets.filter(t => t.target_type === 'stop_loss').length;
  const hasPartialTargets = partialTargets.length > 0;
  
  const handleEdit = useCallback(() => {
    onEditTpSl?.(position);
  }, [onEditTpSl, position]);

  const handleClearTP = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onClearTpSl?.(market, "tp");
  }, [onClearTpSl, market]);

  const handleClearSL = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onClearTpSl?.(market, "sl");
  }, [onClearTpSl, market]);

  return (
    <div className="text-right flex items-center justify-end gap-2">
      <div className="flex flex-col items-end gap-0">
        {/* Standard TP/SL display */}
        {hasTP && (
          <div className="flex items-center gap-1">
            <span className="text-bid-foreground text-[11px] font-medium">
              TP: ${formatCurrency(takeProfit!, 2)}
          </span>
            {onClearTpSl && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearTP}
                className="h-4 w-4 p-0 transition-colors hover:bg-transparent group"
                title="Remove Take Profit"
              >
                <X className="size-3 group-hover:text-ask-foreground" />
              </Button>
            )}
          </div>
        )}
        {hasSL && (
          <div className="flex items-center gap-1">
            <span className="text-ask-foreground text-[11px] font-medium">
              SL: ${formatCurrency(stopLoss!, 2)}
          </span>
            {onClearTpSl && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearSL}
                className="h-4 w-4 p-0 transition-colors hover:bg-transparent group"
                title="Remove Stop Loss"
              >
                <X className="size-3 group-hover:text-ask-foreground" />
              </Button>
            )}
          </div>
        )}
        
        {/* Partial TP/SL summary display */}
        {!hasStandardTpSl && hasPartialTargets && (
          <div className="flex items-center gap-1.5 text-[11px] font-medium">
            {partialTpCount > 0 && (
              <span className="text-bid-foreground">
                {partialTpCount} TP
              </span>
            )}
            {partialTpCount > 0 && partialSlCount > 0 && (
              <span className="text-muted-foreground/50">/</span>
            )}
            {partialSlCount > 0 && (
              <span className="text-ask-foreground">
                {partialSlCount} SL
              </span>
            )}
          </div>
        )}
        
        {/* Loading state */}
        {!hasStandardTpSl && isLoadingPartial && (
          <span className="text-[10px] text-muted-foreground">...</span>
        )}
      </div>
      {onEditTpSl && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleEdit}
          className="h-7 w-7 p-0 hover:bg-transparent transition-colors group"
          title="Edit TP/SL"
        >
          <Pencil className="size-3.5 text-muted-foreground/60 group-hover:text-foreground transition-colors" />
        </Button>
      )}
    </div>
  );
});

// ============================================================================
// STABLE ACTIONS CELL - Rendered OUTSIDE TanStack Table's cell system
// 
// This component is rendered independently of the table's flexRender cycle.
// It receives position data directly (market, side, size, prices) and stable callbacks.
// Now only contains the Close button - Edit TP/SL moved to TpSlCell.
// 
// Industry-standard pattern: data flows through React props, no store lookup needed.
// If the user can see the position, they can close it.
// ============================================================================
const ActionsCell = memo(function ActionsCell({ 
  market,
  side,
  size,
  currentPrice,
  entryPrice,
  leverage,
  margin,
  onClosePosition,
}: { 
  market: string;
  side: "long" | "short";
  size: number;
  currentPrice: number;
  entryPrice: number;
  leverage: number;
  margin: number;
  onClosePosition: (data: ClosePositionData) => void;
}) {
  const handleClose = useCallback(() => {
    onClosePosition({ market, side, size, currentPrice, entryPrice, leverage, margin });
  }, [onClosePosition, market, side, size, currentPrice, entryPrice, leverage, margin]);

  return (
    <div className="flex justify-end">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleClose}
        className="h-7 w-7 p-0 text-muted-foreground hover:text-ask-foreground transition-colors"
        title="Close Position..."
      >
        <X className="size-4" />
      </Button>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if position data changes
  // Price changes don't require re-render for the button itself
  return (
    prevProps.market === nextProps.market &&
    prevProps.side === nextProps.side &&
    prevProps.size === nextProps.size
  );
});

// Memoized Market cell
const MarketCell = memo(function MarketCell({ 
  market, 
  onMarketClick 
}: { 
  market: string;
  onMarketClick: (market: string) => void;
}) {
  const handleClick = useCallback(() => {
    onMarketClick(market);
  }, [onMarketClick, market]);

  return (
    <div className="flex items-center gap-2">
      <span 
        className="font-medium cursor-pointer hover:text-primary hover:underline transition-colors"
        onClick={handleClick}
      >
        {market}
      </span>
    </div>
  );
});

// Memoized Side cell
const SideCell = memo(function SideCell({ side }: { side: string }) {
  const isLong = side === "long";
  return (
    <div
      className={cn(
        "font-medium",
        isLong ? "text-bid-foreground" : "text-ask-foreground"
      )}
    >
      {isLong ? "Long" : "Short"}
    </div>
  );
});

// Memoized static value cells
const SizeCell = memo(function SizeCell({ size }: { size: number }) {
  return (
    <div className="text-right font-medium">
      {formatCurrency(size, 4)}
    </div>
  );
});

const EntryPriceCell = memo(function EntryPriceCell({ 
  price, 
  symbol 
}: { 
  price: number;
  symbol: string;
}) {
  // Get market precision dynamically from backend config
  const { quotePrecision } = useMarketPrecision(symbol);
  return (
    <div className="text-right font-medium">
      ${formatCurrency(price, quotePrecision)}
    </div>
  );
});

const LeverageCell = memo(function LeverageCell({ 
  market,
  position,
  onEditLeverage,
}: { 
  market: string;
  position?: Position;
  onEditLeverage?: (position: Position) => void;
}) {
  // Subscribe directly to Zustand store - like price cells and TP/SL cells do
  // This ensures we always have the latest leverage value from the store
  // When leverage updates, only THIS cell re-renders (not the whole table)
  const leverage = usePositionLeverage(market);
  
  const handleEdit = useCallback(() => {
    if (position && onEditLeverage) {
      onEditLeverage(position);
    }
  }, [onEditLeverage, position]);

  return (
    <div className="text-right font-medium flex items-center justify-end gap-1">
      <span>{leverage}x</span>
      {position && onEditLeverage && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleEdit}
          className="h-7 w-7 p-0 hover:bg-transparent transition-colors group"
          title="Change Leverage"
        >
          <Pencil className="size-3.5 text-muted-foreground/60 group-hover:text-foreground transition-colors" />
        </Button>
      )}
    </div>
  );
});

const MarginCell = memo(function MarginCell({ 
  margin,
  position,
  marginMode,
  onEditMargin,
}: { 
  margin: number;
  position?: Position;
  marginMode: "isolated" | "cross" | null;
  onEditMargin?: (position: Position) => void;
}) {
  const handleEdit = useCallback(() => {
    if (position && onEditMargin) {
      onEditMargin(position);
    }
  }, [onEditMargin, position]);

  // Cross margin: show dash (margin is at account level, not per-position)
  if (marginMode === "cross") {
    return (
      <div className="text-right font-medium text-muted-foreground">
        -
      </div>
    );
  }

  // Only show edit button for isolated margin mode
  const canEditMargin = marginMode === "isolated";

  return (
    <div className="text-right font-medium flex items-center justify-end gap-1">
      <span>${formatCurrency(margin, 2)}</span>
      {position && onEditMargin && canEditMargin && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleEdit}
          className="h-7 w-7 p-0 hover:bg-transparent transition-colors group"
          title="Adjust Position Margin"
        >
          <Pencil className="size-3.5 text-muted-foreground/60 group-hover:text-foreground transition-colors" />
        </Button>
      )}
    </div>
  );
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================

type PositionsProps = {
  data: Position[];
  isLoading: boolean;
  marginMode: "isolated" | "cross" | null;
  onClosePosition: (data: ClosePositionData) => void;
  onEditTpSl?: (position: Position) => void;
  onClearTpSl?: (market: string, type: "tp" | "sl") => void;
  onEditLeverage?: (position: Position) => void;
  onEditMargin?: (position: Position) => void;
  sorting: SortingState;
  onSort: (state: SortingState) => void;
  // NOTE: No priceMap prop - prices come from Zustand store via usePrice()
  // Cells subscribe individually, so only price cells re-render on price changes
};

/**
 * Positions table component.
 * 
 * Memoized to prevent re-renders from parent - only re-renders when props change.
 * Price cells subscribe directly to Zustand store, so price changes don't affect this component.
 * Action buttons are rendered via stable renderActions - NEVER re-rendered by TanStack Table.
 */
export const Positions = memo(function Positions({
  data,
  isLoading,
  marginMode,
  onClosePosition,
  onEditTpSl,
  onClearTpSl,
  onEditLeverage,
  onEditMargin,
  sorting,
  onSort,
}: PositionsProps) {
  const columns = useColumns();

  // Stable render function for action buttons (Close only)
  // This is called by DataTable instead of TanStack Table's flexRender
  // The function itself is stable (useCallback with stable deps)
  // ActionsCell is memoized - only re-renders if position data changes
  // Industry-standard: pass data directly, no store lookup needed
  const renderActions = useCallback((position: Position) => (
    <ActionsCell
      market={position.market}
      side={position.side}
      size={position.size}
      currentPrice={position.currentPrice}
      entryPrice={position.entryPrice}
      leverage={position.leverage}
      margin={position.margin}
      onClosePosition={onClosePosition}
    />
  ), [onClosePosition]);

  // Stable render function for TP/SL cell with edit button
  // This is called by DataTable instead of TanStack Table's flexRender
  // Ensures the edit button remains clickable during price updates
  // TpSlCell subscribes to Zustand store directly for TP/SL values (like price cells)
  const renderTpSl = useCallback((position: Position) => (
    <TpSlCell
      market={position.market}
      position={position}
      onEditTpSl={onEditTpSl}
      onClearTpSl={onClearTpSl}
    />
  ), [onEditTpSl, onClearTpSl]);

  // Stable render function for leverage cell with edit button
  // LeverageCell subscribes to Zustand store directly for leverage value (like price cells)
  const renderLeverage = useCallback((position: Position) => (
    <LeverageCell
      market={position.market}
      position={position}
      onEditLeverage={onEditLeverage}
    />
  ), [onEditLeverage]);

  // Stable render function for margin cell with edit button (isolated mode)
  const renderMargin = useCallback((position: Position) => (
    <MarginCell
      margin={position.margin}
      position={position}
      marginMode={marginMode}
      onEditMargin={onEditMargin}
    />
  ), [marginMode, onEditMargin]);

  return (
    <DataTable
      columns={columns as ColumnDef<Position, unknown>[]}
      data={data}
      isLoading={isLoading}
      sorting={sorting}
      onSort={onSort}
      columnMeta={COLUMN_META}
      renderActions={renderActions}
      renderTpSl={renderTpSl}
      renderLeverage={renderLeverage}
      renderMargin={renderMargin}
    />
  );
});

// ============================================================================
// COLUMN DEFINITIONS - STATIC (no price dependency)
// 
// These columns are created ONCE and never change due to price updates.
// Price cells subscribe to Zustand store via usePrice() selector.
// Only the specific cells that need a price re-render when it changes.
// Headers and action buttons NEVER re-render.
// ============================================================================

const columnHelper = createColumnHelper<Position>();

// Column definitions are now completely static - no callbacks needed
// Actions are rendered via DataTable's renderActions prop, not via column cell
const useColumns = () => {
  const { setProduct } = useProduct();
  const { products } = useProductOptions();
  const navigate = useNavigate();

  // Stable callback for market click
  const handleMarketClick = useCallback((market: string) => {
    const selectedProduct = products.find((p) => p.id === market);
    if (selectedProduct) {
      setProduct(selectedProduct);
      // Navigate to new market URL (replace history to avoid back button cycling through markets)
      navigate(`/trade/${selectedProduct.id}`, { replace: true });
    }
  }, [products, setProduct, navigate]);

  // STATIC columns - no priceMap in dependency array!
  // Columns are created once and never recreated due to price changes.
  const columns = useMemo(
    () => [
      columnHelper.accessor("market", {
        header: (h) => <HeaderCell header={h}>Market</HeaderCell>,
        cell: (props) => (
          <MarketCell 
            market={props.getValue()} 
            onMarketClick={handleMarketClick} 
          />
        ),
      }),
      columnHelper.accessor("side", {
        header: (h) => <HeaderCell header={h}>Side</HeaderCell>,
        cell: (props) => <SideCell side={props.getValue()} />,
      }),
      columnHelper.display({
        id: "positionValue",
        header: (h) => (
          <HeaderCell header={h} align="right">
            Position Value
          </HeaderCell>
        ),
        cell: (props) => (
          <PositionValueCell 
            symbol={props.row.original.market}
            size={props.row.original.size} 
          />
        ),
      }),
      columnHelper.accessor("size", {
        header: (h) => (
          <HeaderCell header={h} align="right">
            Size
          </HeaderCell>
        ),
        cell: (props) => <SizeCell size={props.getValue()} />,
      }),
      columnHelper.accessor("entryPrice", {
        header: (h) => (
          <HeaderCell header={h} align="right">
            Entry Price
          </HeaderCell>
        ),
        cell: (props) => (
          <EntryPriceCell 
            price={props.getValue()} 
            symbol={props.row.original.market} 
          />
        ),
      }),
      columnHelper.display({
        id: "currentPrice",
        header: (h) => (
          <HeaderCell header={h} align="right">
            Current Price
          </HeaderCell>
        ),
        cell: (props) => (
          <CurrentPriceCell symbol={props.row.original.market} />
        ),
      }),
      columnHelper.accessor("leverage", {
        header: (h) => (
          <HeaderCell header={h} align="right">
            Leverage
          </HeaderCell>
        ),
        // Cell content is rendered via DataTable's renderLeverage prop
        // This ensures the leverage value and edit button update correctly from store
        cell: () => null,
      }),
      columnHelper.accessor("margin", {
        header: (h) => (
          <HeaderCell header={h} align="right">
            Position Margin
          </HeaderCell>
        ),
        // Cell content is rendered via DataTable's renderMargin prop
        // This ensures the margin value and edit button show correctly based on margin mode
        cell: () => null,
      }),
      columnHelper.display({
        id: "unrealizedPnl",
        header: (h) => (
          <HeaderCell header={h} align="right">
            Unrealized P&L
          </HeaderCell>
        ),
        cell: (props) => (
          <UnrealizedPnlCell position={props.row.original} />
        ),
      }),
      columnHelper.accessor("liquidationPrice", {
        header: (h) => (
          <HeaderCell header={h} align="right">
            Liq. Price
          </HeaderCell>
        ),
        cell: (props) => (
          <LiqPriceCell 
            symbol={props.row.original.market}
            liqPrice={props.getValue()}
            isLong={props.row.original.side === "long"}
          />
        ),
      }),
      columnHelper.display({
        id: "tpsl",
        header: (h) => (
          <HeaderCell header={h} align="right">
            TP / SL
          </HeaderCell>
        ),
        // Cell content is rendered via DataTable's renderTpSl prop
        // This ensures the edit button is NEVER re-rendered by TanStack Table updates
        cell: () => null,
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="text-right">Close</div>,
        // Cell content is rendered via DataTable's renderActions prop
        // This ensures actions are NEVER re-rendered by TanStack Table updates
        cell: () => null,
      }),
    ],
    [handleMarketClick] // Truly static - no callbacks that could change
  );

  return columns;
};

